// @ts-nocheck - Large configuration file with complex dynamic types, converted from JS
import {} from "react-redux";
import { loadContactSetsPaginated, proxyAPI } from "../../../../proxyAPI.ts";
import {
  compoundInputs,
  dataCategoryInputs,
  inputArgumentInput,
  valueFromInput,
} from "../Inputs/inputs";
import timezones from "../timezones";
import {
  selectBusinessHoursList,
  selectCounterCategories,
  selectDataCategories,
  selectEventHandlers,
  selectInputArguments,
  selectMessageTemplates,
  selectMilestoneLabels,
  selectWorkflow,
} from "../../../../store/slices/projectSlice";
import { store } from "../../../../store/store";
import { pageProps as defaultPageProps } from "../../../../helpers/pageProps";
import type {
  LogicConfig,
  LogicInputConfig,
  LogicOptionsConfigResult,
} from "../Inputs/optionsConfigTypes";

/** A single input configuration entry inside a condition's `inputs` array. */
export type ConditionInputConfig = LogicInputConfig;

/** The configuration object for a single condition type. */
export type ConditionConfig = LogicConfig;

/** Parameters accepted by `conditionOptionsConfig`. */
export interface ConditionOptionsConfigParams {
  condition: Record<string, any>;
  context: Record<string, any>;
  params?: Record<string, any>;
  rule?: Record<string, any>;
  event?: string | Record<string, any>;
  dispatch?: (...args: any[]) => void;
  workflowUUID?: string;
  pageProps?: Record<string, any>;
}

export type ConditionOptionsConfigResult = LogicOptionsConfigResult;

const normalizeConditionOptionsConfigParams = (
  paramsOrCondition:
    | ConditionOptionsConfigParams
    | ConditionOptionsConfigParams["condition"],
  context?: ConditionOptionsConfigParams["context"],
  event?: ConditionOptionsConfigParams["event"],
  workflowUUID?: ConditionOptionsConfigParams["workflowUUID"],
  pageProps?: ConditionOptionsConfigParams["pageProps"]
): ConditionOptionsConfigParams => {
  if (
    paramsOrCondition &&
    typeof paramsOrCondition === "object" &&
    "condition" in paramsOrCondition &&
    "context" in paramsOrCondition
  ) {
    return paramsOrCondition;
  }

  return {
    condition: paramsOrCondition || {},
    context: context || {},
    event,
    params: { workflowUUID },
    workflowUUID,
    pageProps,
  };
};

const conditionOptionsConfig = (
  paramsOrCondition:
    | ConditionOptionsConfigParams
    | ConditionOptionsConfigParams["condition"],
  contextArg?: ConditionOptionsConfigParams["context"],
  eventArg?: ConditionOptionsConfigParams["event"],
  workflowUUIDArg?: ConditionOptionsConfigParams["workflowUUID"],
  pagePropsArg?: ConditionOptionsConfigParams["pageProps"]
): ConditionOptionsConfigResult => {
  const {
    condition,
    context,
    event,
    params = {},
    workflowUUID,
    pageProps = defaultPageProps,
  } = normalizeConditionOptionsConfigParams(
    paramsOrCondition,
    contextArg,
    eventArg,
    workflowUUIDArg,
    pagePropsArg
  );
  const resolvedWorkflowUUID = params.workflowUUID || workflowUUID;
  const state = store.getState();
  const projectData = state?.project || {};
  const { countries, defaultCountry } = pageProps;
  const { contactFields, reportingTopics } = context;

  const dataCategories = selectDataCategories(state) || [];
  const eventHandlers = selectEventHandlers(state) || [];
  const workflow = selectWorkflow(resolvedWorkflowUUID)(state);
  const inputArguments = selectInputArguments(state) || [];
  const businessHoursList = selectBusinessHoursList(state) || [];
  const milestones = selectMilestoneLabels(state) || [];
  const counters = selectCounterCategories(state) || [];
  const messageTemplates = selectMessageTemplates(state) || [];

  const getOptionValues = (optionType) =>
    [...(condition.Options || [])]
      .filter((actOpt) => actOpt.Type === optionType)
      .sort((a, b) => a.Order - b.Order)
      .map((opt) => opt.Value);

  const dateInputs = [
    {
      type: "compound",
      field: "ConditionDateTime",
      defaultValue: [
        {
          ValueFrom: "static",
          Value: new Date()
            .toISOString()
            .replace(/\:\d{2}\.\d{3}Z$/, "")
            .replace("T", " "),
        },
      ],
      props: {
        label: "Condition date/time",
        inputType: "dateTime",
        dateTimeType: "dateTime",
        inputArgumentProps: { Format: ["date", "dateTime", "time"] },
        isValidNewOption: () => false,
        inputHelp: "",
        customValueFromOptions: [{ value: "now", label: "Now" }],
      },
    },
    {
      type: "compound",
      field: "ConditionTimeZone",
      defaultValue: [""],
      props: {
        label: "Override condition time zone",
        inputType: "select",
        isMulti: false,
        isValidNewOption: () => false,
        inputArgumentProps: {
          Format: ["configuration"],
          DataStructure: "Time Zones",
        },
        inputHelp: "",
        options: [
          {
            value: "",
            label:
              [...(condition.Options || [])].find(
                (actOpt) => actOpt.Type === "ConditionDateTime"
              )?.ValueFrom === "inputArgument"
                ? `Variable Default (${
                    inputArguments.find(
                      (ia) =>
                        ia.UUID ===
                        [...(condition.Options || [])].find(
                          (actOpt) => actOpt.Type === "ConditionDateTime"
                        )?.Value
                    )?.DefaultTimeZone ||
                    `Project default: ${pageProps.defaultTZ}`
                  })`
                : `Project default (${pageProps.defaultTZ})`,
          },
          ...timezones.map((tz) => ({
            value: tz.tzCode,
            label: tz.label,
          })),
        ],
      },
    },
    {
      type: "compound",
      field: "Offset",
      defaultValue: [
        {
          ValueFrom: "static",
          Value: "0",
          Unit: "minute",
          Sign: "positive",
        },
      ],
      props: {
        label: "Offset",
        type: "number",
        inputArgumentProps: { Format: ["number"] },
        isValidNewOption: () => false,
        inputHelp: "",
        showUnitInput: true,
        showSignInput: true,
      },
    },

    // operator
    {
      type: "property",
      field: "Operator",
      defaultValue: ["compare:eq"],
      optionDefaults: ([v]) => ({
        BusinessHoursUUID: null,
        weekDay: null,
        dateTime:
          v === "InsideBusinessHours"
            ? null
            : {
                ValueFrom: "static",
                Value: new Date()
                  .toISOString()
                  .replace(/\:\d{2}\.\d{3}Z$/, "")
                  .replace("T", " "),
              },
      }),
      props: {
        label: "Operator",
        inputType: "select",
        isValidNewOption: () => false,
        options: [
          {
            value: "compare:eq",
            label: "is equal to",
          },
          {
            value: "compare:gt",
            label: "is greater than",
          },
          {
            value: "compare:gte",
            label: "is greater than or equal to",
          },
          {
            value: "compare:lt",
            label: "is less than",
          },
          {
            value: "compare:lte",
            label: "is less than or equal to",
          },
          {
            label: "week day",
            value: "weekDay",
            ifOptionTypeHas: [
              {
                optionType: "ConditionDateTime",
                values: ["currentTime"],
              },
            ],
          },
          ...(inputArguments.find((arg) => arg.UUID === condition.TypeUUID)
            ?.Format !== "time"
            ? [
                {
                  label: "week day",
                  value: "weekDay",
                  ifOptionTypeHas: [
                    {
                      optionType: "ConditionDateTime",
                      values: ["inputArgument"],
                    },
                  ],
                },
              ]
            : []),
          {
            value: "InsideBusinessHours",
            label: "within business hours",
          },
        ],
      },
    },
    {
      // business hours selection
      type: "property",
      field: "BusinessHoursUUID",
      ifPropertyHas: [
        {
          field: "Operator",
          values: ["InsideBusinessHours"],
        },
      ],
      props: {
        label: "Business hours",
        inputType: "select",
        isValidNewOption: () => false,
        options: businessHoursList.map((opt) => ({
          value: opt.UUID,
          label: opt.Label || opt.UUID,
        })),
      },
    },
    ...(condition.Operator !== "InsideBusinessHours"
      ? [
          {
            type: "compound",
            field: "dateTime",
            defaultValue: [
              {
                ValueFrom: "static",
                Value: new Date()
                  .toISOString()
                  .replace(/\:\d{2}\.\d{3}Z$/, "")
                  .replace("T", " "),
              },
            ],
            props: {
              label: "Date/time",
              inputType: "dateTime",
              dateTimeType: "dateTime",
              inputArgumentProps: { Format: ["date", "dateTime", "time"] },
              isValidNewOption: () => false,
              inputHelp: "",
            },
          },
        ]
      : []),

    {
      type: "option",
      field: "weekDay",
      ifPropertyHas: [
        {
          field: "Operator",
          values: ["weekDay"],
        },
      ],
      props: {
        label: "Week day",
        inputHelp: "Select week day",
        isMulti: true,
        inputType: "select",
        isValidNewOption: () => false,
        options: [
          { value: "Monday", label: "Monday" },
          { value: "Tuesday", label: "Tuesday" },
          { value: "Wednesday", label: "Wednesday" },
          { value: "Thursday", label: "Thursday" },
          { value: "Friday", label: "Friday" },
          { value: "Saturday", label: "Saturday" },
          { value: "Sunday", label: "Sunday" },
        ],
      },
    },
    {
      type: "option",
      field: "monthStart",
      ifOptionTypeHas: [
        {
          optionType: "dataType",
          values: ["monthStart"],
        },
        { optionType: "dateTimeFrom", values: ["fixed"] },
      ],
      props: {
        label: "Month start",
        type: "number",
        min: 0,
        max: 31,
        inputHelp: "",
      },
    },
    {
      type: "option",
      field: "monthEnd",
      ifOptionTypeHas: [
        {
          optionType: "dataType",
          values: ["monthEnd"],
        },
        { optionType: "dateTimeFrom", values: ["fixed"] },
      ],
      props: {
        label: "Month end",
        inputHelp: "",
        type: "number",
        min: 0,
        max: 31,
      },
    },

    {
      type: "compound",
      field: "TimeZone",
      defaultValue: [""],
      props: {
        label: `Time zone ${
          condition.Operator === "InsideBusinessHours"
            ? "for business hours"
            : ""
        }`,
        inputType: "select",
        isMulti: false,
        isValidNewOption: () => false,
        inputArgumentProps: {
          Format: ["configuration"],
          DataStructure: "Time Zones",
        },
        inputHelp: "",
        options: [
          {
            value: "",
            label:
              condition.Operator === "InsideBusinessHours"
                ? `Business hours default (${
                    businessHoursList.find(
                      (ia) => ia.UUID === condition.BusinessHoursUUID
                    )?.TimeZone || `Project default: ${pageProps.defaultTZ}`
                  })`
                : [...(condition.Options || [])].find(
                    (actOpt) => actOpt.Type === "dateTime"
                  )?.ValueFrom === "inputArgument"
                ? `Variable default (${
                    inputArguments.find(
                      (ia) =>
                        ia.UUID ===
                        [...(condition.Options || [])].find(
                          (actOpt) => actOpt.Type === "dateTime"
                        )?.Value
                    )?.DefaultTimeZone ||
                    `Project default: ${pageProps.defaultTZ}`
                  })`
                : `Project default (${pageProps.defaultTZ})`,
          },
          ...timezones.map((tz) => ({
            value: tz.tzCode,
            label: tz.label,
          })),
        ],
      },
    },
  ];

  const countOperators = [
    {
      value: "notempty",
      label: "answered",
    },
    {
      value: "empty",
      label: "not answered",
    },
    {
      value: "compare:eq",
      label: "is equal to",
    },
    {
      value: "compare:gt",
      label: "is greater than",
    },
    {
      value: "compare:gte",
      label: "is greater than or equal to",
    },
    {
      value: "compare:lt",
      label: "is less than",
    },
    {
      value: "compare:lte",
      label: "is less than or equal to",
    },
  ];

  const countInputs = [
    // operator
    {
      type: "property",
      field: "Operator",
      defaultValue: ["compare:eq"],
      ifOptionTypeHas: [],
      props: {
        label: "Operator",
        inputType: "select",
        isValidNewOption: () => false,
        options: countOperators.slice(2),
      },
    },

    // single number input
    {
      type: "option",
      field: "count",
      defaultValue: ["0"],
      ifPropertyHas: [
        {
          field: "Operator",
          values: [
            "compare:eq",
            "compare:gt",
            "compare:gte",
            "compare:lt",
            "compare:lte",
          ],
        },
      ],
      props: {
        label: "Value",
        inputHelp: "Enter a number",
        type: "number",
        isMulti: false,
      },
    },
  ];

  const countInputsWithoutBetween = [
    // operator
    {
      type: "property",
      field: "Operator",
      defaultValue: ["compare:eq"],
      ifOptionTypeHas: [],
      props: {
        label: "Operator",
        inputType: "select",
        isValidNewOption: () => false,
        options: countOperators.slice(0, -1),
      },
    },
    // value from
    valueFromInput({
      ifPropertyHas: [
        {
          field: "Operator",
          values: [
            "compare:eq",
            "compare:gt",
            "compare:gte",
            "compare:lt",
            "compare:lte",
          ],
        },
      ],
    }),

    {
      // data category
      type: "option",
      field: "storageData:category",
      ifOptionTypeHas: [
        {
          optionType: "valueFrom",
          values: ["storageData:category"],
        },
      ],
      isDataCategory: true,
      props: {
        label: "Data category",
        inputType: "select",
        isMulti: true,
        isValidNewOption: () => false,
        inputHelp: "Select a data category",
        options: dataCategories.map((cat) => ({
          value: cat,
          label: cat,
        })),
      },
    },

    {
      // input arguments
      type: "option",
      field: "inputArgument",
      ifOptionTypeHas: [
        {
          optionType: "valueFrom",
          values: ["inputArgument"],
        },
      ],
      props: {
        label: "Variable",
        inputType: "select",
        isValidNewOption: () => false,
        options: inputArguments.map((arg) => ({
          value: arg.UUID,
          label: arg.Label,
        })),
      },
    },
    // single number input
    {
      type: "option",
      field: "count",
      ifPropertyHas: [
        {
          field: "Operator",
          values: [
            "compare:eq",
            "compare:gt",
            "compare:gte",
            "compare:lt",
            "compare:lte",
          ],
        },
      ],
      ifOptionTypeHas: [
        {
          optionType: "valueFrom",
          values: ["fixed"],
        },
      ],
      props: {
        label: "Count",
        inputHelp: "Enter a number",
        type: "number",
        isMulti: false,
      },
    },
  ];

  const formQuestions = [];
  if (workflow) {
    workflow.EventListeners?.forEach((el) => {
      const eh = eventHandlers.find((e) => e.UUID === el.EventHandlerUUID);
      eh?.Rules?.forEach((rule) => {
        rule.Actions?.forEach((act) => {
          if (act.Type === "form-question") {
            formQuestions.push(
              act.Options?.reduce(
                (prev, curr) => {
                  if (curr.Type === "static-choice") {
                    return { ...prev, Choices: [...prev.Choices, curr.Value] };
                  }
                  return { ...prev, [curr.Type]: curr.Value };
                },
                { UUID: act.UUID, Choices: [] }
              )
            );
          }
        });
      });
    });
  }

  const selectedQuestion = formQuestions.find(
    (q) =>
      q.UUID ===
      condition.Options?.find((opt) => opt.Type === "question")?.Value
  );
  const questionType = selectedQuestion?.Type;

  const questionChoices = selectedQuestion?.Choices || [];

  const textInputs = ({
    dataField = "storageData:category",
    threadField = "UseMainThread",
    includeAnswered = true,
  } = {}) => [
    // all operators
    {
      type: "property",
      field: "Operator",
      defaultValue: ["keyword"],
      ifOptionTypeHas: [],
      optionDefaults: ([v]) => ({
        value: [
          "keyword",
          "starts-with",
          "contains",
          "ends-with",
          "one-of",
          "all-of",
          "none-of",
        ].includes(v)
          ? { ValueFrom: "static-options", Value: "" }
          : null,
      }),
      props: {
        label: "Operator",
        inputType: "select",
        isValidNewOption: () => false,
        options: [
          ...(includeAnswered
            ? [
                { value: "empty", label: "empty" },
                { value: "notempty", label: "not empty" },
              ]
            : []),
          { value: "keyword", label: "keyword", ifOptionTypeHas: [] },
          { value: "starts-with", label: "starts with", ifOptionTypeHas: [] },
          { value: "ends-with", label: "ends with", ifOptionTypeHas: [] },
          { value: "contains", label: "contains", ifOptionTypeHas: [] },
          { value: "one-of", label: "one of", ifOptionTypeHas: [] },
          {
            value: "all-of",
            label: "all of",
            ifOptionTypeHas: [],
            ifPropertyHas: [
              {
                field: "Type",
                values: ["inputArgument", "InputArgumentDT"],
              },
            ],
          },
          { value: "none-of", label: "none of", ifOptionTypeHas: [] },
          // ...countOperators.slice(2),
        ],
      },
    },

    {
      type: "compound",
      field: "value",
      defaultValue: [{ ValueFrom: "static-options", Value: "" }],
      ifPropertyHas: [
        {
          field: "Operator",
          values: [
            "keyword",
            "starts-with",
            "contains",
            "ends-with",
            "one-of",
            "all-of",
            "none-of",
          ],
        },
      ],
      optionDefaults: ([{ Value: v, ValueFrom }]) => ({
        "static-choice": null,
        DefaultValue: null,
        DefaultValueType: "ignore",
        "choice-handler":
          ValueFrom === "storageData:category-raw" ? "none" : null,
      }),
      className: "rounded-end-0 me-n2",
      props: {
        label: "Choices",
        showStaticOptionsSelector: true,
        showStaticSelector: false,
        maxLength: 255,
        isValidNewOption: (v) => false,
        onCreateOption: (v) => v.trim(),
        inputArgumentFilter: (arg) => ["short-text"].includes(arg.Format),
      },
    },
    // show static options if the question has static options
    ...((condition.Options || [])
      .filter((actOpt) => actOpt.Type === "value")
      .sort((a, b) => a.Order - b.Order)
      .map((opt) => opt.ValueFrom)
      .includes("static-options")
      ? [
          {
            type: "option",
            field: "static-choice",
            defaultValue: [""],
            className: "rounded-start-0",
            props: {
              label: "Static Options",
              isMulti: true,
              maxLength: 255,
              isValidNewOption: (v) => v.trim(),
              onCreateOption: (v) => v.trim(),
              valueFrom: "value",
              inputType: "select",
              options:
                inputArguments
                  ?.find(
                    (ia) =>
                      ia.UUID === getOptionValues("variable")[0] &&
                      ia.IsSelect &&
                      ["one-of", "all-of", "none-of"].includes(
                        condition.Operator
                      )
                  )
                  ?.SelectOptions?.map((opt) => ({ value: opt, label: opt })) ??
                [],
            },
          },
        ]
      : []),

    ...countInputs.slice(2),
  ];

  const selectedContactField = contactFields?.find(
    (f) => f.uuid === getOptionValues("contactFieldUUID")[0]
  );

  const fieldChannels = (cf) => [
    ...(cf?.isSms ? ["sms"] : []),
    ...(cf?.isEmail ? ["email"] : []),
    ...(cf?.isVoice ? ["voice"] : []),
  ];

  return {
    "file:name-exists": {
      label: "File Name Exists",
      inputs: [
        {
          type: "compound",
          field: "file:name",
          defaultValue: [""],
          props: {
            label: "File name",
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (value) => value.trim(),
          },
        },
        {
          type: "option",
          field: "file:scope",
          defaultValue: ["either"],
          props: {
            label: "Scope",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "either", label: "Either" },
              { value: "temporary", label: "Temporary" },
              { value: "permanent", label: "Permanent" },
            ],
          },
        },
      ],
    },
    "file:have-tags": {
      label: "Files Have Tags",
      inputs: [
        {
          type: "compound",
          field: "variable",
          defaultValue: [""],
          props: {
            label: "File variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "binary" },
            isValidNewOption: (value) => value.trim(),
          },
        },
        {
          type: "compound",
          field: "file:tags",
          defaultValue: [""],
          props: {
            label: "Tags",
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (value) => value.trim(),
          },
        },
      ],
    },
    // "ticket:is-set": {
    //   label: "Ticket Context Is Set",
    // },
    "ticket:exists": {
      label: "Ticket Exists",
      inputs: [
        {
          type: "compound",
          field: "ticket",
          props: {
            label: "Ticket variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "ticket" },
          },
        },
      ],
    },
    // "ticket:is-topic": {
    //   label: "Ticket Context belongs to Topic",
    //   inputs: [
    //     {
    //       type: "option",
    //       field: "Topic",
    //       optionDefaults: () => ({
    //         editFrom: "fixed",
    //         editValue: null,
    //       }),
    //       defaultValue: [""],
    //       props: {
    //         label: "Topic",
    //         isSelect: true,
    //         options: [
    //           { value: "", label: "Any" },
    //           ...(reportingTopics?.map((f) => ({
    //             value: f.uuid,
    //             label: f.label,
    //           })) ?? []),
    //         ],
    //       },
    //     },
    //     // ticket statuses (all statuses in all topics)
    //     {
    //       type: "option",
    //       field: "TicketStatus",
    //       optionDefaults: () => ({
    //         editFrom: "fixed",
    //         editValue: null,
    //       }),
    //       defaultValue: [""],
    //       props: {
    //         label: "Ticket status",
    //         isSelect: true,
    //         options: [
    //           { value: "", label: "Any" },
    //           ...[
    //             ...new Set(
    //               reportingTopics?.map((t) => t.ticketStatuses).flat(1)
    //             ),
    //           ].map((s) => ({
    //             value: s,
    //             label: s,
    //           })),
    //         ],
    //       },
    //     },
    //   ],
    // },
    // validate-number
    "validate:phone-number": {
      label: "Validate phone number",
      inputs: [
        {
          type: "option",
          field: "default:country-ISO-code",
          defaultValue: [""],
          props: {
            label: "Default country",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              {
                value: "",
                label: `Default from product settings (${defaultCountry.country_name})`,
              },
              ...countries.map((c) => ({
                value: c.country_code,
                label: c.country_name,
              })),
            ],
          },
        },
        {
          type: "option",
          field: "must:country-ISO-code",
          props: {
            label: "Must be from country",
            inputType: "select",
            isMulti: true,
            isValidNewOption: () => false,
            options: countries.map((c) => ({
              value: c.country_code,
              label: c.country_name,
            })),
          },
        },
        {
          type: "property",
          field: "Operator",
          ifOptionTypeHas: [],
          defaultValue: ["one-of"],
          props: {
            label: "Operator",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "one-of", label: "one of" },
              { value: "none-of", label: "none of" },
            ],
          },
        },
        {
          type: "option",
          field: "Type",
          props: {
            label: "Phone number type",
            inputType: "select",
            isMulti: true,
            isValidNewOption: () => false,
            options: [
              { value: "mobile", label: "mobile" },
              { value: "fixed-line", label: "fixed line" },
              { value: "fixed-line-or-mobile", label: "fixed line or mobile" },
              { value: "voip", label: "VoIP" },
              { value: "unknown", label: "unknown" },
            ],
          },
        },
        {
          type: "compound",
          field: "number",
          defaultValue: [""],
          props: {
            label: "Phone number",
            isInputArgumentOnly: true,
            inputArgumentFilter: (arg) => ["short-text"].includes(arg.Format),
          },
        },
      ],
    },

    // form submit - handled by backend
    "form:reply": {
      label: "Form reply",
      events: ["form-submitted"],
      inputs: [
        // question
        {
          type: "option",
          field: "question",
          propertyDefaults: () => ({
            Operator: "notempty",
          }),
          optionDefaults: () => ({
            value: null,
            valueFrom: null,
          }),
          props: {
            label: "Question",
            inputType: "select",
            isMulti: false,
            isValidNewOption: () => false,
            inputHelp: "Select the question",
            options: formQuestions.map((question) => ({
              value: question.UUID,
              label: question.Title,
            })),
          },
        },

        // text inputs
        ...(questionType === "text" ? textInputs() : []),

        // rating options
        ...(questionType === "rating" ? countInputsWithoutBetween : []),

        ...(["radiogroup", "checkbox", "dropdown", "tagBox"].includes(
          questionType
        )
          ? [
              {
                type: "property",
                field: "Operator",
                ifOptionTypeHas: [],
                props: {
                  label: "Operator",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    {
                      value: "empty",
                      label: "not answered",
                    },
                    {
                      value: "notempty",
                      label: "answered",
                    },
                    { value: "one-of", label: "one of" },
                    {
                      value: "none-of",
                      label: "none of",
                    },
                    {
                      value: "all-of",
                      label: "all of",
                    },
                  ],
                },
              },
              // value from
              valueFromInput({
                ifPropertyHas: [
                  {
                    field: "Operator",
                    values: ["one-of", "none-of", "all-of"],
                  },
                ],
              }),

              // data category
              ...dataCategoryInputs({
                context,
                ifOptionTypeHas: [
                  {
                    optionType: "valueFrom",
                    values: ["storageData:category"],
                  },
                ],
              }),

              // input arguments
              inputArgumentInput({
                context,
                ifOptionTypeHas: [
                  {
                    optionType: "valueFrom",
                    values: ["inputArgument"],
                  },
                ],
              }),

              {
                type: "option",
                field: "value",
                ifPropertyHas: [
                  {
                    field: "Operator",
                    values: ["one-of", "none-of", "all-of"],
                  },
                ],
                ifOptionTypeHas: [
                  {
                    optionType: "valueFrom",
                    values: ["fixed"],
                  },
                ],
                props: {
                  label: "Choices",
                  inputType: questionChoices.length > 0 ? "select" : undefined,
                  isMulti: true,
                  isValidNewOption: (v) => v && questionChoices.length === 0,
                  onCreateOption: (option) => option,
                  inputHelp: "Select choices",
                  options: questionChoices.map((c) => ({ value: c, label: c })),
                },
              },
            ]
          : []),

        ...(["file", "signaturepad"].includes(questionType)
          ? [
              {
                type: "property",
                field: "Operator",
                ifOptionTypeHas: [],
                props: {
                  label: "Operator",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    {
                      value: "notempty",
                      label: "answered",
                    },
                    {
                      value: "empty",
                      label: "not answered",
                    },
                  ],
                },
              },
            ]
          : []),
      ],
    },

    // handled by survey.js
    "form-validation": {
      label: "Form reply",
      events: ["form-render"],
      inputs: [
        {
          type: "option",
          field: "question",
          propertyDefaults: () => ({
            Operator: "notempty",
          }),
          optionDefaults: () => ({
            value: null,
          }),
          props: {
            label: "Question",
            inputType: "select",
            isMulti: false,
            isValidNewOption: () => false,
            inputHelp: "Select the question",
            options: formQuestions.map((question) => ({
              value: question.UUID,
              label: question.Title,
            })),
          },
        },

        {
          type: "property",
          field: "Operator",
          ifOptionTypeHas: [],
          defaultValue: ["notempty"],
          props: {
            label: "Operator",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "notempty", label: "answered" },
              { value: "empty", label: "not answered" },
              ...(questionType === "text" ||
              questionType === "rating" ||
              questionType === "dropdown" ||
              questionType === "radiogroup"
                ? [
                    { value: "=", label: "is equal to" },
                    { value: "<>", label: "is not equal to" },
                  ]
                : []),
              ...(questionType === "rating"
                ? [
                    { value: ">", label: "greater than" },
                    { value: ">=", label: "greater than or equal to" },
                    { value: "<", label: "less than" },
                    { value: "<=", label: "less than or equal to" },
                  ]
                : []),
              { value: "anyof", label: "one of" },
            ],
          },
        },

        // fixed value options

        {
          // operator = and <>
          // show choices for radiogroup and checkbox
          // multi if checkbox
          type: "option",
          field: "value",
          ifPropertyHas: [
            {
              field: "Operator",
              values: ["=", "<>"],
            },
          ],
          props: {
            label: "Choices",
            inputType:
              ["radiogroup", "checkbox", "dropdown", "tagBox"].includes(
                questionType
              ) && questionChoices.length > 0
                ? "select"
                : undefined,
            isValidNewOption: (v) => v && questionChoices.length === 0,
            inputHelp: "Select choices",
            options: questionChoices.map((c) => ({ value: c, label: c })),
          },
        },

        {
          // operator anyof
          type: "option",
          field: "value",
          ifPropertyHas: [
            {
              field: "Operator",
              values: ["anyof"],
            },
          ],
          props: {
            label: "Choices",
            inputType:
              ["radiogroup", "checkbox", "dropdown", "tagBox"].includes(
                questionType
              ) && questionChoices.length > 0
                ? "select"
                : undefined,
            isMulti: true,
            isValidNewOption: (v) =>
              v &&
              (questionType === "text" ||
                questionType === "rating" ||
                questionChoices.length === 0),
            onCreateOption: (option) => option,
            inputHelp: "Select choices",
            // show Choices if checkbox or radio
            ...(["radiogroup", "checkbox", "dropdown", "tagBox"].includes(
              questionType
            )
              ? {
                  options: questionChoices.map((c) => ({ value: c, label: c })),
                }
              : {}),
          },
        },

        {
          // other operators "contains", "notcontains", ">", ">=", "<", "<="
          type: "option",
          field: "value",
          ifPropertyHas: [
            {
              field: "Operator",
              values: ["contains", "notcontains", ">", ">=", "<", "<="],
            },
          ],
          props: {
            label: "Value",
            isMulti: false,
            inputHelp: "Type something",
          },
        },
      ],
    },

    "recipients:count": {
      label: "Recipient count",
      inputs: [
        {
          type: "option",
          field: "recipientType",
          defaultValue: ["workflow"],
          props: {
            label: "Recipient type",
            inputType: "select",
            options: [
              // { value: "context", label: "Contacts in runtime context" },
              { value: "workflow", label: "Contacts in workflow sendlist" },
              { value: "template", label: "Contacts in message templates" },
            ],
          },
        },
        {
          type: "option",
          field: "templateUUID",
          defaultvalue: [messageTemplates[0]?.UUID || ""],
          ifOptionTypeHas: [
            {
              optionType: "recipientType",
              values: ["template"],
            },
          ],
          props: {
            label: "Message template",
            inputType: "select",
            options: messageTemplates.map((t) => ({
              value: t.UUID,
              label: t.Label,
            })),
          },
        },
        ...countInputs,
      ],
    },

    // Check the count of contacts matching the filter in the current workflow runtime. This can be compared to a fixed number or to a number variable.
    // {
    // 	"Type": "workflow:contacts-count",
    // 	"Operator": "compare:eq/compare:gt/compare:gte/compare:lt/compare:lte",
    // 	"OutputVariableUuid": "4d79f59f-116a-4b9d-a275-240aab119242", // we can store the actual count that matched in this variable, number format only, single value expected
    // 	"OutputVariableUseMainThread": false,
    // 	"Options": [
    // 		// the number to compare to, either fixed or from number variable (single or multiple)
    // 		{"Type": "variable", "Value": "abc-123-uuid", "UseMainThread": false, "ValueFrom": "inputArgument"},
    // 	],
    // }

    "workflow:contacts-count": {
      label: "Check the count of contacts",
      inputs: [
        {
          type: "property",
          field: "Operator",
          defaultValue: ["compare:eq"],
          ifOptionTypeHas: [],
          props: {
            label: "Operator",
            inputType: "select",
            isValidNewOption: () => false,
            options: countOperators.slice(2),
          },
        },
        {
          type: "compound",
          field: "variable",
          defaultValue: ["0"],
          props: {
            label: "Number to compare",
            type: "number",
            inputArgumentProps: {
              Format: ["number"],
            },
          },
        },
      ],
    },

    "reply:message": {
      label: "Reply",
      events: ["message:reply"],
      inputs: textInputs({ includeAnswered: false }),
    },

    broadcastSuccess: {
      label: "Contacts for broadcast selected successfully",
      inputs: [
        {
          type: "option",
          field: "ruleUUID",
          defaultValue: [
            eventHandlers.reduce(
              (prev, eh) => [
                ...prev,
                ...(eh.Rules?.filter((r) =>
                  r.Actions?.some((a) => a.Type === "start:broadcast")
                ).map((r) => r.UUID) ?? []),
              ],
              []
            )[0],
          ],
          props: {
            label: "Rule",
            inputType: "select",
            // get rules where there is a broadcast action
            options: eventHandlers.reduce(
              (prev, eh) => [
                ...prev,
                ...(eh.Rules?.filter((r) =>
                  r.Actions?.some((a) => a.Type === "start:broadcast")
                ).map((r) => ({ value: r.UUID, label: r.Label })) ?? []),
              ],
              []
            ),
          },
        },
      ],
    },

    escalationAvailable: {
      label: "Current escalation available (TBC)",
    },

    "variable:text": {
      label: "Variable",
      inputs: [
        {
          type: "compound",
          field: "variable",
          props: {
            label: "Variable",
            isInputArgumentOnly: true,
            inputArgumentFilter: (arg) => ["short-text"].includes(arg.Format),
          },
        },

        ...(["date", "dateTime", "time"].includes(
          inputArguments.find(
            (arg) => arg.UUID === getOptionValues("variable")[0]
          )?.Format
        )
          ? dateInputs
          : []),

        ...(["short-text", "sms", "voice", "email"].includes(
          inputArguments.find(
            (arg) => arg.UUID === getOptionValues("variable")[0]
          )?.Format
        )
          ? textInputs()
          : []),

        ...(["number"].includes(
          inputArguments.find(
            (arg) => arg.UUID === getOptionValues("variable")[0]
          )?.Format
        )
          ? countInputsWithoutBetween
          : []),
      ],
    },

    dateTime: {
      label: "Date time",
      inputs: [...dateInputs],
    },

    triggerChannel: {
      label: "Trigger channel",
      inputs: [
        {
          type: "option",
          field: "triggerChannel",
          props: {
            label: "Trigger channel",
            inputHelp: "Select trigger channels from the list.",
            inputType: "select",
            isMulti: true,
            isValidNewOption: () => false,
            options: [
              { label: "sms", value: "sms" },
              { label: "email", value: "email" },
              { label: "voice", value: "voice" },
              { label: "http", value: "http" },
              { label: "manual", value: "manual" },
            ],
          },
        },
      ],
    },

    "contact:has-value": {
      label: "Check if contact has value",
      inputs: [
        {
          type: "compound",
          field: "contact",
          props: {
            label: "Contact variable to be checked",
            inputType: "select",
            isInputArgumentOnly: true,
            inputArgumentProps: {
              Format: ["sms", "voice", "email", "contact"],
            },
          },
        },

        {
          type: "option",
          field: "contactFieldUUID",
          defaultValue: ["status"],
          props: {
            label: "Contact field",
            inputType: "select",
            options: [
              { value: "status", label: "Status (system)" },
              ...contactFields?.map((f) => ({
                value: f.uuid,
                label: f.label,
              })),
            ],
          },
        },
        {
          type: "compound",
          field: "value",
          defaultValue: [{ ValueFrom: "static-options" }],
          className: "rounded-end-0 me-n2",
          props: {
            label: "Value",
            showStaticOptionsSelector: true,
            showStaticSelector: false,
            inputArgumentProps: {
              Format: ["short-text", "number"],
            },
          },
        },
        ...((condition.Options || [])
          .filter((actOpt) => actOpt.Type === "value")
          .map((opt) => opt.ValueFrom)
          .includes("static-options")
          ? [
              {
                type: "option",
                field: "static-choice",
                defaultValue: [""],
                className: "rounded-start-0",
                props: {
                  label: "Static Options",
                  isMulti: true,
                  maxLength: 255,
                  isValidNewOption: (v) => v.trim(),
                  onCreateOption: (v) => v.trim(),
                  valueFrom: "value",
                },
              },
            ]
          : []),
      ],
    },

    // {
    // 		"Type": "component:package",
    // 		"OutputVariableUuid": "4d79f59f-116a-4b9d-a275-240aab119242", // we can store the actual label, format, guid that matched in this variable, short-text format only, single value expected
    // 		"Options": [

    // 		]
    // 	}
    "component:package": {
      label: "Check component package",
      events: ["component:package"],
      inputs: [
        // 		"Operator": "label/format/from",
        {
          type: "property",
          field: "Operator",
          defaultValue: ["label"],
          ifOptionTypeHas: [],
          optionDefaults: () => ({
            label: null,
            format: null,
            from: null,
          }),
          props: {
            label: "Operator",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              {
                value: "label",
                label: "Label match",
              },
              {
                value: "format",
                label: "Format match",
              },
              {
                value: "from",
                label: "Sender's GUID match",
              },
            ],
          },
        },

        // 			// WHEN operator is label, static label value to compare, can be multiple, like a keyword match, if ANY of the labels match then condition is true
        // 			{ "Type": "label", "Value": "expected package label 1"},
        // 			{ "Type": "label", "Value": "expected package label 2"},
        {
          type: "option",
          field: "label",
          ifPropertyHas: [
            {
              field: "Operator",
              values: ["label"],
            },
          ],
          props: {
            label: "Label",
            isMulti: true,
            isValidNewOption: (v) => !!v.trim(),
            onCreateOption: (v) => v.trim(),
          },
        },
        // 			// for label only: if true then label checks if expected label is contained in the package label,
        // 			// if false then expected label must be an exact match to package label
        // 			{ "Type": "contains", "Value": "true"/"false"},
        {
          type: "option",
          field: "contains",
          defaultValue: ["true"],
          ifPropertyHas: [
            {
              field: "Operator",
              values: ["label"],
            },
          ],
          props: {
            label: "contains",
            inputType: "radio",
            radioName: "component-package-contains",
            options: [
              { value: "true", label: "contains" },
              { value: "false", label: "exact match" },
            ],
          },
        },
        // 			// WHEN operator is format, variable format value to compare, can be multiple, static only, if ANY of the formats match then condition is true
        // 			{ "Type": "format", "Value": "short-text"},
        // 			{ "Type": "format", "Value": "number"},
        {
          type: "option",
          field: "format",
          ifPropertyHas: [
            {
              field: "Operator",
              values: ["format"],
            },
          ],
          props: {
            label: "Format",
            isMulti: true,
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "short-text", label: "short-text" },
              { value: "number", label: "number" },
              { value: "json", label: "json" },
              { value: "geo-json", label: "geo-json" },
              { value: "email", label: "email" },
              { value: "sms", label: "sms" },
              { value: "voice", label: "voice" },
              { value: "date", label: "date" },
              { value: "time", label: "time" },
              { value: "dateTime", label: "dateTime" },
              { value: "contact", label: "contact" },
              { value: "asset", label: "asset" },
              { value: "event", label: "event" },
              { value: "data-entry", label: "reporting data entry" },
              { value: "binary", label: "file" },
              { value: "configuration", label: "configuration" },
            ],
          },
        },
        // 			// WHEN operator is from, short-text variable containing expected sender's GUID, can be multiple, if ANY of the GUIDs match then condition is true
        // 			{ "Type": "from", "Value": "abc-123-uuid", "ValueFrom": "variable", "UseMainThread": false}
        {
          type: "compound",
          field: "from",
          ifPropertyHas: [
            {
              field: "Operator",
              values: ["from"],
            },
          ],
          props: {
            label: "Sender's GUID",
            isInputArgumentOnly: true,
            inputArgumentProps: {
              Format: "short-text",
            },
          },
        },
      ],
    },

    isMilestone: {
      label: "Is milestone reached",
      events: ["milestone"],
      inputs: [
        {
          type: "option",
          field: "isMilestone",
          props: {
            label: "Milestone",
            inputType: "select",
            isMulti: true,
            isValidNewOption: () => false,
            options: milestones.map((m) => ({ value: m, label: m })),
          },
        },
      ],
    },

    "milestone:is-pending": {
      label: "Is milestone pending",
      inputs: [
        {
          type: "option",
          field: "isMilestone",
          props: {
            label: "Milestone",
            inputType: "select",
            isMulti: true,
            isValidNewOption: () => false,
            options: milestones.map((m) => ({ value: m, label: m })),
          },
        },
      ],
    },

    StatusIs: {
      label: "Status is",
      events: ["outbound-delivery-status"],
      inputs: [
        {
          type: "option",
          field: "StatusIs",
          props: {
            label: "Status",
            isMulti: true,
            isValidNewOption: () => false,
            options: [
              { value: "Success", label: "Success" },
              { value: "Failed", label: "Failed" },
              { value: "Undeliverable", label: "Undeliverable" },
              { value: "Expired", label: "Expired" },
              { value: "Pending", label: "Pending" },
            ],
          },
        },
      ],
    },

    // Compare two numbers.
    // First number can be a number variable or count of items in a variable.
    // Second number can be a number variable, count of items in a variable, OR fixed number.
    // {
    // 	"Type": "number:compare",
    "number:compare": {
      label: "Compare numbers",
      // 	"Operator": "compare:eq/compare:gt/compare:gte/compare:lt/compare:lte", (no between)
      // 	"Options": [
      inputs: [
        // 		// first number source
        // 		{ "Type": "number", "Value": "abc-123-uuid", "ValueFrom": "inputArgument", "UseMainThread": false},
        {
          type: "compound",
          field: "number",
          optionDefaults: ([{ Value: v }]) => ({
            "number:compare-type": inputArguments.some(
              (arg) =>
                arg.UUID === v && arg.Format === "number" && arg.IsMultiple
            )
              ? "value"
              : null,
          }),
          props: {
            label: "First number to compare",
            isInputArgumentOnly: true,
          },
        },
        // 		 // if the "number" is a multiple number variable, then we count how many numbers are in it
        // 		 // or if ANY of the numbers match the condition
        // 		{ "Type": "number:compare-type", "Value": "value"/"count",},
        ...(inputArguments.some(
          (arg) =>
            arg.UUID === getOptionValues("number")[0] &&
            arg.Format === "number" &&
            arg.IsMultiple
        )
          ? [
              {
                type: "option",
                field: "number:compare-type",
                defaultValue: ["value"],
                props: {
                  label: "Value / Count",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { value: "value", label: "Value of number" },
                    { value: "count", label: "Count of items" },
                  ],
                },
              },
            ]
          : []),
        // 		// operator
        {
          type: "property",
          field: "Operator",
          defaultValue: ["compare:eq"],
          ifOptionTypeHas: [],
          props: {
            label: "Operator",
            inputType: "select",
            isValidNewOption: () => false,
            options: countOperators.slice(2),
          },
        },

        // 		// second number source
        // 		{ "Type": "number-to", "Value": "-1", "ValueFrom": "static"},
        // 		 // if the "number-to" is a multiple number variable, then we count how many numbers are in it
        // 		 // or if ANY of the numbers match the condition
        {
          type: "compound",
          field: "number-to",
          defaultValue: ["0"],
          optionDefaults: ([{ Value: v }]) => ({
            "number-to:compare-type": inputArguments.some(
              (arg) =>
                arg.UUID === v && arg.Format === "number" && arg.IsMultiple
            )
              ? "value"
              : null,
          }),
          props: {
            label: "Second number to compare",
          },
        },
        // 		{ "Type": "number-to:compare-type", "Value": "value"/"count",},
        ...(inputArguments.some(
          (arg) =>
            arg.UUID === getOptionValues("number-to")[0] &&
            arg.Format === "number" &&
            arg.IsMultiple
        )
          ? [
              {
                type: "option",
                field: "number-to:compare-type",
                defaultValue: ["value"],
                props: {
                  label: "Value / Count",
                  inputType: "select",
                  isValidNewOption: () => false,
                  options: [
                    { value: "value", label: "Value of number" },
                    { value: "count", label: "Count of items" },
                  ],
                },
              },
            ]
          : []),
        // 	]
      ],
      // }
    },

    "api:request": {
      label: "API request",
      events: ["api:request"],
      inputs: [],
    },

    //    Check if contact is in variable, group or context.
    // 		{
    // 			"Type": "contact:in-variable/contact:in-group/contact:in-context/starter:in-variable/starter:in-group/starter:in-context/recipient:in-variable/recipient:in-group/recipient:in-context",
    // 			"Options": [
    // 				// WHEN the type is *:in-group, one group selected
    // 				{ "Type": "ContactSet", "Value": "abc-123-uuid"},

    // 				// WHEN the type is *:in-variable, one variable selected
    // 				// single or multiple contact/Email/SMS/Voice variable
    // 				{ "Type": "variable", "Value": "abc-123-uuid", "UseMainThread": false},
    // 			]
    // 		}

    "contact:in": {
      label: "Contact is in",
      inputs: [
        {
          type: "option",
          field: "who",
          defaultValue: ["contact"],
          optionDefaults: ([{ Value: v }]) => ({
            variable: null,
          }),
          props: {
            label: "Who",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "contact", label: "Contact" },
              ...([
                "message:reply",
                "outbound-delivery-status",
                "form-render",
                "form-submitted",
                "form-accessed",
              ].includes(event)
                ? [{ value: "recipient", label: "Recipient" }]
                : []),
              { value: "starter", label: "Starter" },
            ],
          },
        },
        {
          type: "compound",
          field: "variable",
          ifOptionTypeHas: [
            {
              optionType: "who",
              values: ["contact"],
            },
          ],
          props: {
            label: "Contact variable",
            isInputArgumentOnly: true,
            inputArgumentProps: { Format: "contact" },
          },
        },
        {
          type: "option",
          field: "where",
          defaultValue: ["variable"],
          optionDefaults: ([{ Value: v }]) => ({
            variableTo: null,
            group: null,
          }),
          props: {
            label: "In",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "variable", label: "Contact Variable" },
              { value: "group", label: "Group" },
              { value: "group-variable", label: "Group Variable" },
            ],
          },
        },
        {
          type: "compound",
          field: "variableTo",
          ifOptionTypeHas: [
            {
              optionType: "where",
              values: ["variable"],
            },
          ],
          props: {
            label: "Contact Variable",
            isInputArgumentOnly: true,
            inputArgumentFilter: (arg) =>
              ["email", "sms", "voice", "contact"].includes(arg.Format) &&
              arg.UUID !== getOptionValues("variable")[0],
          },
        },
        {
          type: "compound",
          field: "variableTo",
          ifOptionTypeHas: [
            {
              optionType: "where",
              values: ["group-variable"],
            },
          ],
          props: {
            label: "Group Variable",
            isInputArgumentOnly: true,
            inputArgumentFilter: (arg) =>
              ["group"].includes(arg.Format) &&
              arg.UUID !== getOptionValues("variable")[0],
          },
        },
        {
          // groups
          type: "option",
          field: "group",
          ifOptionTypeHas: [
            {
              optionType: "where",
              values: ["group"],
            },
          ],
          props: {
            label: "Group",
            inputType: "select",
            isMulti: true,
            isValidNewOption: () => false,
            isContactSet: true,
            inputHelp: "Select a group",
            loadOptions: loadContactSetsPaginated(),
            getValueLabels: async (vs) => {
              const values = [];
              // eslint-disable-next-line no-restricted-syntax
              for (const v of vs) {
                if (v) {
                  // eslint-disable-next-line no-await-in-loop
                  const data = await proxyAPI({
                    prefix: "contacts/v1",
                    url: `/contact-set/${v}`,
                  });
                  values.push({ value: data.uuid, label: data.label });
                }
              }
              return values;
            },
          },
        },
      ],
    },
    "error:type-is": {
      label: "Error type is",
      events: ["process:error"],
      inputs: [
        {
          type: "option",
          field: "value",
          props: {
            label: "Error types",
            inputType: "select",
            isMulti: true,
            isValidNewOption: () => false,
            options: [
              { value: "internal", label: "Internal" },
              { value: "resource", label: "Resource" },
              { value: "validation", label: "Validation" },
              { value: "throttling", label: "Throttling" },
            ],
          },
        },
      ],
    },
    "error:message-matches": {
      label: "Error message matches",
      events: ["process:error"],
      inputs: [
        {
          type: "property",
          field: "Operator",
          defaultValue: ["contains"],
          props: {
            label: "Match",
            inputType: "select",
            isValidNewOption: () => false,
            options: [
              { value: "one-of", label: "One of" },
              { value: "none-of", label: "None of" },
              { value: "keyword", label: "Keyword" },
              { value: "contains", label: "Contains" },
            ],
          },
        },
        {
          type: "compound",
          field: "value",
          defaultValue: [""],
          props: {
            label: "Message value",
            inputArgumentProps: { Format: "short-text" },
            isValidNewOption: (v) => v.trim(),
          },
        },
      ],
    },
  };
};

export default conditionOptionsConfig;

// variable count condition
// variable compare
